<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { bulkUpdateHourlyDABids } from '@/services/eos'
import type { IHourlyDABidCreationForm } from '@/types/eos/forms'
import { useQueryClient } from '@tanstack/vue-query'
import { ElMessage, type FormInstance } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, reactive, ref } from 'vue'

const visible = defineModel<boolean>()

const props = defineProps<{ site: ISiteIdentifier; dateTime: DateTime; nbpt?: string }>()

const queryClient = useQueryClient()

const confirming = ref<boolean>(false)

const formRef = ref<FormInstance>()
const form = reactive({
  price_usd: undefined,
  load_mw: undefined,
  pr_strike_price_usd: undefined,
  pr_load_mw: undefined,
  energy_market_non_curtailable_load_mw: undefined,
  ancillary_services_market_non_curtailable_load_mw: undefined,
  site_id: '',
})

const dialogTitle = computed(() => `Bulk Update Hourly DA Bid for ${props.site.site_name}`)
const startHour = computed(() => props.dateTime.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ'))
const endHour = computed(() => props.dateTime.plus({ hours: 23 }).toFormat('yyyy-MM-dd HH:mm:ss ZZZZ'))

const confirm = async () => {
  try {
    await formRef.value?.validate()
  } catch {
    // validate throws an error
    return
  }

  confirming.value = true

  const hours = []
  for (let i = 0; i < 24; i++) {
    hours.push({
      site_id: props.site.site_id!,
      for_hour_utc: props.dateTime.plus({ hours: i }).toUTC().toISO()!,
      price_usd: form.price_usd,
      load_mw: form.load_mw,
      pr_strike_price_usd: form.pr_strike_price_usd,
      pr_load_mw: form.pr_load_mw,
      energy_market_non_curtailable_load_mw: form.energy_market_non_curtailable_load_mw,
      ancillary_services_market_non_curtailable_load_mw: form.ancillary_services_market_non_curtailable_load_mw,
    })
  }

  const creationForm: IHourlyDABidCreationForm = {
    site_id: props.site.site_id!,
    hourly_da_bids: hours,
  }

  const dateDisplayString = props.dateTime.toFormat('yyyy-MM-dd (ZZZZ)')
  try {
    await bulkUpdateHourlyDABids(creationForm)
    ElMessage.success(`Successfully updated daily DA bids for ${dateDisplayString}`)
    queryClient.invalidateQueries({ queryKey: ['eos', 'table'] })
  } catch {
    ElMessage.error(`Failed to update daily DA bids for ${dateDisplayString}.`)
  } finally {
    visible.value = false
  }
}

const handleDialogClosed = () => {
  formRef.value?.resetFields()
  confirming.value = false
}
</script>

<template>
  <el-dialog
    v-model="visible"
    :title="dialogTitle"
    :width="656"
    :show-close="false"
    :close-on-click-modal="false"
    @closed="handleDialogClosed"
  >
    <el-form ref="formRef" :model="form" label-position="left" label-width="auto">
      <el-form-item label="Hourly Range">
        <div class="w-full flex flex-row justify-between items-center gap-2">
          <el-input readonly disabled :input-style="{ 'text-align': 'center' }" :value="startHour" />
          <span>to</span>
          <el-input readonly disabled :input-style="{ 'text-align': 'center' }" :value="endHour" />
        </div>
      </el-form-item>
      <el-form-item label="NBPT Price">
        <el-input :value="nbpt" readonly disabled class="text-center">
          <template #suffix>$</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Price" prop="price_usd">
        <el-input-number v-model="form.price_usd" class="w-full" :precision="2" :min="0.0" controls-position="right">
          <template #suffix>$</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="Load" prop="load_mw">
        <el-input-number v-model="form.load_mw" class="w-full" :precision="3" :min="0.0" controls-position="right">
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="PR Strike Price" prop="pr_strike_price_usd">
        <el-input-number
          v-model="form.pr_strike_price_usd"
          class="w-full"
          :precision="2"
          :min="0.0"
          controls-position="right"
        >
          <template #suffix>$</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="PR Load" prop="pr_load_mw">
        <el-input-number v-model="form.pr_load_mw" class="w-full" :precision="3" :min="0.0" controls-position="right">
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="Energy Market Non-Curtailable Load" prop="energy_market_non_curtailable_load_mw">
        <el-input-number
          v-model="form.energy_market_non_curtailable_load_mw"
          class="w-full"
          :precision="3"
          :min="0.0"
          controls-position="right"
        >
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="ASM Non-Curtailable Load" prop="ancillary_services_market_non_curtailable_load_mw">
        <el-input-number
          v-model="form.ancillary_services_market_non_curtailable_load_mw"
          class="w-full"
          :precision="3"
          :min="0.0"
          controls-position="right"
        >
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="confirming" @click="visible = false">Cancel</el-button>
      <el-button type="primary" :loading="confirming" @click="confirm"> Confirm </el-button>
    </template>
  </el-dialog>
</template>
